/** Máscaras e validações de campos brasileiros. */

export const onlyDigits = (v) => String(v ?? '').replace(/\D/g, '');

/** 52998224725 → 529.982.247-25 (formata parcialmente enquanto digita) */
export function maskCpf(v) {
  const d = onlyDigits(v).slice(0, 11);
  return d
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d{1,2})$/, '.$1-$2');
}

/** Mesma regra do backend (dígitos verificadores). */
export function isValidCpf(v) {
  const cpf = onlyDigits(v);
  if (!/^\d{11}$/.test(cpf) || /^(\d)\1{10}$/.test(cpf)) return false;
  const d = [...cpf].map(Number);
  const check = (len) => {
    let sum = 0;
    for (let i = 0; i < len; i++) sum += d[i] * (len + 1 - i);
    const rest = (sum * 10) % 11;
    return rest === 10 ? 0 : rest;
  };
  return check(9) === d[9] && check(10) === d[10];
}

/** (11) 99999-0000 ou (11) 3333-0000 */
export function maskPhone(v) {
  const d = onlyDigits(v).slice(0, 11);
  if (d.length <= 2) return d.length ? `(${d}` : '';
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

/** CNPJ numérico ou alfanumérico (Receita, jul/2026): só [A-Z0-9], até 14. */
export const normalizeCnpj = (v) => String(v ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 14);

/** 12ABC34501DE35 → 12.ABC.345/01DE-35 (formata parcialmente enquanto digita) */
export function maskCnpj(v) {
  const s = normalizeCnpj(v);
  const parts = [s.slice(0, 2), s.slice(2, 5), s.slice(5, 8), s.slice(8, 12), s.slice(12, 14)];
  let out = parts[0];
  if (parts[1]) out += `.${parts[1]}`;
  if (parts[2]) out += `.${parts[2]}`;
  if (parts[3]) out += `/${parts[3]}`;
  if (parts[4]) out += `-${parts[4]}`;
  return out;
}

/** Mesma regra do backend: módulo 11 com valor = código ASCII − 48. */
export function isValidCnpj(v) {
  const cnpj = normalizeCnpj(v);
  if (!/^[A-Z0-9]{12}\d{2}$/.test(cnpj) || /^(\d)\1{13}$/.test(cnpj)) return false;
  const values = [...cnpj].map((c) => c.charCodeAt(0) - 48);
  const dv = (len) => {
    const weights = len === 12 ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const rest = weights.reduce((acc, w, i) => acc + values[i] * w, 0) % 11;
    return rest < 2 ? 0 : 11 - rest;
  };
  return dv(12) === values[12] && dv(13) === values[13];
}

/**
 * Texto livre do veículo (marca, modelo, cor, apelido): só letras, números e um espaço entre palavras.
 * Traço, ponto, barra, emoji etc. viram espaço. Mesma regra do backend (alnum).
 * typing=true: enquanto digita, mantém o espaço do fim (senão não dá para digitar a próxima palavra).
 */
export function alnumText(v, { typing = false } = {}) {
  const s = String(v ?? '').replace(/[^\p{L}\p{N}]+/gu, ' ').replace(/^ /, '');
  return typing ? s : s.trim();
}

/** Placa antiga (ABC1234) ou Mercosul (ABC1D23), sem hífen. */
export const normalizePlate = (v) => String(v ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 7);
export const maskPlate = normalizePlate;
export const isValidPlate = (v) => /^[A-Z]{3}\d[A-Z0-9]\d{2}$/.test(normalizePlate(v));

/** RENAVAM: 11 dígitos (antigos de 9 ganham zeros à esquerda). Mesma regra do backend. */
export function normalizeRenavam(v) {
  const d = onlyDigits(v);
  return d.length === 9 || d.length === 10 ? d.padStart(11, '0') : d;
}

export function isValidRenavam(v) {
  const r = normalizeRenavam(v);
  if (!/^\d{11}$/.test(r) || /^(\d)\1{10}$/.test(r)) return false;
  const w = [3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const dv = (w.reduce((acc, x, i) => acc + Number(r[i]) * x, 0) * 10) % 11;
  return (dv === 10 ? 0 : dv) === Number(r[10]);
}

export const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v ?? '').trim());

/** Política do user pool: 8+ caracteres, maiúscula, minúscula e número. */
export function passwordProblems(p) {
  const s = String(p ?? '');
  const problems = [];
  if (s.length < 8) problems.push('8 caracteres');
  if (!/[A-Z]/.test(s)) problems.push('uma letra maiúscula');
  if (!/[a-z]/.test(s)) problems.push('uma letra minúscula');
  if (!/\d/.test(s)) problems.push('um número');
  return problems;
}
